import { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDocumentManagementStore } from '@/stores/documentManagement/documentManagement.store';
import { useInstitutionsStore } from '@/stores/institutions';
import { useToastStore } from '@/stores/toast';
import { ERouterNames, TFormBuilderEditSchemaCamel } from '@/types';

/**
 * Hook with the form builder template submit handlers.
 * `onSubmit` creates a new template (`formBuilderId` is "empty") or updates the existing one,
 * then navigates to Document Management. If the request fails or the template to update
 * isn't found, an error toast is shown and the user stays on the page.
 * @returns The `onSubmit` and `onError` handlers for react-hook-form's `handleSubmit`.
 */
export const useFormBuilderSubmit = () => {
  const { formBuilderId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setToast = useToastStore((state) => state.setToast);
  const currentInstitutionId = useInstitutionsStore(
    (state) => state.currentInstitution?.id,
  );
  const {
    getFormBuilderTemplate,
    createFormBuilderTemplate,
    updateFormBuilderTemplate,
  } = useDocumentManagementStore((state) => ({
    createFormBuilderTemplate: state.createFormBuilderTemplate,
    getFormBuilderTemplate: state.getFormBuilderTemplate,
    updateFormBuilderTemplate: state.updateFormBuilderTemplate,
  }));

  const { state } = useLocation();

  const showErrorToast = () =>
    setToast({
      title: t('error'),
      description: t('something-went-wrong-please-try-again'),
      variant: 'error',
    });

  const onSubmit = async (data: TFormBuilderEditSchemaCamel) => {
    if (!formBuilderId) return;
    try {
      if (formBuilderId !== 'empty') {
        const value = await getFormBuilderTemplate(formBuilderId.toString());
        if (!value) {
          showErrorToast();
          return;
        }
        await updateFormBuilderTemplate({
          ...value,
          data,
        });
      } else {
        await createFormBuilderTemplate({
          institutionId: currentInstitutionId ?? '',
          isActive: true,
          data,
          userType: state?.userType,
        });
      }
      navigate(ERouterNames.DOCUMENT_MANAGEMENT);
    } catch (error) {
      console.error(error);
      showErrorToast();
    }
  };

  const onError = (errors: FieldErrors<TFormBuilderEditSchemaCamel>) =>
    console.error('❌ ERRORS:', errors);

  return { onSubmit, onError };
};
